import endsWith from 'lodash/endsWith';
import isError from 'lodash/isError';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';
import reduce from 'lodash/reduce';
import startsWith from 'lodash/startsWith';

import { getConfigValue } from '@pressreader/config';
import { ViewName } from '@pressreader/navigation-types';

import {
    CONFIG_INTERFACE_CURRENT_LANGUAGE,
    CONFIG_INTERFACE_LANGUAGES,
    FEATURE_FLAG_MULTILANGUAGE_ENABLED,
    FEATURE_FLAG_MULTILANGUAGE_PAGES,
} from './constants';
import { getHashPrefix, getRawLocation, getRoutingMode } from './routing.context';

// TODO: move to generic utils
export function extractValue<T = unknown>(obj: Record<string, unknown> = {}, propName: string) {
    const val = obj[propName];
    delete obj[propName];
    return val as T;
}

export const PATH_SEPARATOR = '/';
export const QUERY_SEPARATOR = '?';
export const QUERY_PAIR_SEPARATOR = '&';
export const QUERY_NAME_VALUE_SEPARATOR = '=';

export function echo<T>(val: T) {
    return val;
}

export function echoAsync<T>(val: T) {
    return Promise.resolve(val);
}

/**
 * Ensure that given object is a Error or convert to one if not.
 * @param  err Error object or error message.
 * @return Error object.
 */
export function ensureError(err: string | Error) {
    if (isError(err)) {
        return err;
    }

    return new Error(err);
}

/**
 * Clone given object casting its own enumerable properties to strings.
 * Properties which values equal to null or undefined might be either omitted or substituted with empty string.
 * Used in url generating and route creating.
 * @param  obj Object to clone.
 * @param  forceEmpty If set to true, properties with null and undefined values are substituted by empty string, omitted otherwise.
 * @return Cloned object.
 */
export function clonePlain<T extends Record<string | number | symbol, unknown>>(obj: T, forceEmpty = false) {
    return reduce(
        obj,
        (plain, value, key: keyof T) => {
            if (value !== undefined && value !== null) {
                plain[key] = String(value);
            } else if (forceEmpty) {
                plain[key] = '';
            }

            return plain;
        },
        {} as Record<keyof T, string>,
    );
}

/**
 * Ensure that given string does not start which given separator and end with one.
 * If given string equals to the separator, it's returned "as is".
 * @param  str String to check.
 * @param  separator Separator string.
 * @return A string which starts from something other than a separator and ends with one.
 */
export function normalizeSeparation(str: string, separator: string) {
    if (str === separator) {
        return str;
    }

    if (startsWith(str, separator)) {
        str = str.substring(separator.length);
    }

    if (endsWith(str, separator)) {
        return str;
    }

    return `${str}${separator}`;
}

/**
 * Split string by last occurrence of given separator.
 * @param  str String to split.
 * @param  separator Pattern to split by.
 * @return Array of string segments.
 */
export function splitByLast(str: string, separator: string): [string, string] {
    const delimiterIndex = str.lastIndexOf(separator);
    if (delimiterIndex < 0) {
        return [str, ''];
    }

    return [str.substring(0, delimiterIndex), str.substring(delimiterIndex + 1)];
}

/**
 * Parse url query string (param1=1&param2=2).
 * @param  queryString Query string to parse.
 * @return Parsed name-value map.
 */
export function parseQuery(queryString: string) {
    const query: Record<string, string> = {};
    if (!isString(queryString) || !queryString.length || queryString === QUERY_SEPARATOR) {
        return query;
    }

    if (queryString.indexOf(QUERY_SEPARATOR) === 0) {
        queryString = queryString.substring(1);
    }

    return queryString.split(QUERY_PAIR_SEPARATOR).reduce((parsed, pairStr) => {
        const [name, value] = pairStr.split(QUERY_NAME_VALUE_SEPARATOR, 2);
        parsed[name] = value;
        return parsed;
    }, query);
}

// Characters which should remain unencoded refer https://tools.ietf.org/html/rfc3986#section-3.3 for more details
const uriPathSafe = new RegExp('([^@$&+:])*', 'gi');

/**
 * Perform a "percent"-encoding for a path segment.
 * Unlike encodeURIComponent keeps certain characters, such as 'at' (@) unencoded, similar to encodeURI.
 * @param input component of a URI.
 * @return Encoded component of a URI.
 */
export function encodePathComponent(input: string) {
    return input.replace(uriPathSafe, part => encodeURIComponent(part));
}

/**
 * Perform a "persent"-encoding for a query string parameter.
 * Encode all unsafe symbols in the exact way as encodeURIComponent does.
 * @param uriComponent Decoded component of a URI.
 * @return Encoded component of a URI.
 */
export const encodeQueryParameter = encodeURIComponent;

/**
 * Decode query string and also additionaly take care of replacing '+' with space symbol.
 * https://www.w3.org/Addressing/URL/4_URI_Recommentations.html "Query strings" paragraph.
 * @param str Encoded query string parameter.
 * @return Decoded query string parameter.
 */
export function decodeQueryParameter(str: string) {
    if (isString(str)) {
        str = str.replace(/\+/g, '%20').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    return decodeURIComponent(str);
}

/**
 * The value a query string gives under the first of `names` carrying one, decoded the way declared
 * route parameters are, or `undefined` when none of the names carries a value.
 *
 * A url reads the same here as through a template declaring the parameter: names are matched
 * decoded, the last occurrence of a name wins and a name given without a value counts as none.
 * An undecodable name or value counts as none as well, so reading never throws.
 */
export function readQueryValue(queryString: string, names: readonly string[]): string | undefined {
    const raw = parseQuery(queryString);
    const byName = new Map<string, string | undefined>();
    Object.keys(raw).forEach(rawName => {
        const name = decodeQueryParameterSafely(rawName);
        if (name !== undefined) {
            byName.set(name, raw[rawName]);
        }
    });

    for (const name of names) {
        const rawValue = byName.get(name);
        const value = rawValue ? decodeQueryParameterSafely(rawValue) : undefined;
        if (value !== undefined) {
            return value;
        }
    }

    return undefined;
}

/** `decodeQueryParameter` answering `undefined` instead of throwing on malformed percent-encoding. */
function decodeQueryParameterSafely(str: string) {
    try {
        return decodeQueryParameter(str);
    } catch {
        return undefined;
    }
}

/**
 * Concatenate url path segments and optional query string.
 * @param  path Array of url path segments.
 * @param  queryPairs Array of name-value pairs representing url query string.
 * @return Combined url string.
 */
export function combineRouteUrl(path: string[] = [], queryPairs: [string, string][] = []) {
    let combined = path.map(encodePathComponent).join(PATH_SEPARATOR);

    if (queryPairs.length) {
        const queryStr = queryPairs
            .map(([name, value]) => `${encodeQueryParameter(name)}${QUERY_NAME_VALUE_SEPARATOR}${encodeQueryParameter(value)}`)
            .join(QUERY_PAIR_SEPARATOR);
        combined = `${combined}${QUERY_SEPARATOR}${queryStr}`;
    }

    // Return the combined string, replacing encoded commas (%2C) with actual commas.
    // This is done to keep the URL more readable and user-friendly
    return combined.replace(/%2C/g, ',');
}

/**
 * window.basePath is specified in global scope by the server.
 * In hash routing mode basePath is irrelevant — navigation happens
 * entirely within the hash fragment, so we always return '/'.
 * Legacy code alert!!
 */
export function getBasePath(): string {
    // In hash and memory modes there is no server-injected base path — navigation
    // happens entirely within the fragment / in memory, so the base is always '/'.
    if (getRoutingMode() !== 'browser') {
        return '/';
    }
    return (window as { basePath?: string }).basePath || '/';
}

/**
 * Builds absolute path to be used in browser.
 * Add base path to given url.
 * If page does not have a basePath, a leading slash is added.
 * @param  path - relative path.
 * @return absolute path.
 */
export function withAbsolutePath(path: string) {
    if (startsWith(path, getBasePath())) {
        return path;
    }
    if (startsWith(path, PATH_SEPARATOR)) {
        path = path.substring(1);
    }

    return `${getBasePath()}${path}`;
}

/**
 * Removes base path to given url.
 * If page does not have a basePath, a leading slash is added.
 * @param  path - absolute path.
 * @return relative path.
 */
export function withRelativePath(path: string) {
    const base = getBasePath();
    let result = startsWith(path, base) ? path.substring(base.length) : path;
    if (getRoutingMode() === 'hash') {
        // Strip the configured hash marker ('#' + prefix); fall back to a bare '#'
        // for non-prefixed fragments. Mirrors HashHistoryBackend, so a non-empty
        // hashPrefix (e.g. 'pr=') leaves a clean fragment-relative path rather than
        // 'pr=/...'.
        const marker = hashMarker();
        if (startsWith(result, marker)) {
            result = result.substring(marker.length);
        } else if (startsWith(result, '#')) {
            result = result.substring(1);
        }
    }
    return result;
}

// '#' + the configured hash prefix — the marker HashHistoryBackend writes
// ('#' by default, '#pr=' for hashPrefix 'pr=').
function hashMarker(): string {
    return `#${getHashPrefix()}`;
}

// The current in-fragment route path (with search), with the hash marker stripped
// — mirrors HashHistoryBackend.getLocation(). A fragment outside the marker
// (a host #anchor / foreign hash) resolves to root.
function currentHashPath(): string {
    const marker = hashMarker();
    const hash = getRawLocation().hash || `${marker}/`;
    return startsWith(hash, marker) ? hash.slice(marker.length) : '/';
}

/**
 * Returns the search/query portion of the current URL.
 * In hash routing mode, extracts search from the hash (e.g., `#/search?query=foo` → `?query=foo`).
 * In browser mode, returns `window.location.search`.
 */
export function getLocationSearch(): string {
    if (getRoutingMode() === 'hash') {
        const path = currentHashPath();
        const searchIndex = path.indexOf('?');
        return searchIndex >= 0 ? path.slice(searchIndex) : '';
    }
    return getRawLocation().search;
}

/**
 * Return current path and search relative to basePath.
 * Without starting '/'.
 * Url is returned "as is", no encoding/decoding is applied.
 * @return Relative url
 */
export function getCurrentPath() {
    if (getRoutingMode() === 'hash') {
        return currentHashPath();
    }
    const { pathname, search } = getRawLocation();
    const path = withRelativePath(pathname);
    return `${path}${search}`;
}

/**
 * Return true if current document location is root of application.
 * @return true if root
 */
export function isRootLocation() {
    if (getRoutingMode() === 'hash') {
        const path = currentHashPath();
        return path === '/' || path === '';
    }
    const path = getRawLocation().pathname.replace(getBasePath(), '');
    return path.length === 0;
}

const slugNotAllowedRegExp = /[;/\\?:@&=+$,<>#%.!*'"()[\]{}^`~–‘’“”»«]+/g;

export const generateSlug = memoize((value: string) => {
    if (!isString(value)) {
        throw new Error(`Cannot generate slug for non-string value. Given ${value}`);
    }

    const slug = value
        .trim()
        .replace(slugNotAllowedRegExp, '')
        .replace(/[| -]+/g, '-');

    return encodeURI(slug).toLowerCase();
});

export function updateUlrWithLangCode(url: string, langCode: string) {
    const multiLanguageEnabled = getConfigValue<boolean>(FEATURE_FLAG_MULTILANGUAGE_ENABLED, false);
    if (!multiLanguageEnabled) {
        return url;
    }

    const interfaceLanguages: { id: string; slug: string | null }[] = getConfigValue(CONFIG_INTERFACE_LANGUAGES, []);
    const interfaceLanguage = interfaceLanguages.find(l => l.id === langCode);

    // Returns the rewritten path segments, or null when the URL has no addressable localized form
    // and must redirect to the catalog (callers then drop the rest of the path and the query).
    const replaceLangPrefix = (parts: string[]): string[] | null => {
        const firstPart = parts.length > 0 ? parts[0].toLowerCase() : '';
        const matched = firstPart ? interfaceLanguages.find(l => l.id === firstPart) : undefined;
        if (matched) {
            parts.shift();
        }
        if (matched && !matched.slug && langCode === '') {
            // Slug-less languages are not URL-addressable (SEO allowlist); on canonicalization the
            // URL collapses to the catalog. langCode === '' scopes this to the router's inbound
            // call; language-switch (fonts.ts) passes a non-empty langCode and keeps re-prefixing.
            return null;
        }
        if (interfaceLanguage?.slug) {
            parts.unshift(interfaceLanguage.slug);
        }
        return parts;
    };

    // In hash routing mode the lang prefix lives inside the hash fragment.
    if (getRoutingMode() === 'hash') {
        const hashIndex = url.indexOf('#');
        const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : '';
        const hashContent = hashIndex >= 0 ? url.slice(hashIndex + 1) : url;
        const prefix = beforeHash || getBasePath();

        const parts = replaceLangPrefix(hashContent.split(PATH_SEPARATOR).filter(e => e));
        if (parts === null) {
            return `${prefix}#/${ViewName.StoreHub}`;
        }
        return parts.length ? `${prefix}#/${parts.join(PATH_SEPARATOR)}` : prefix;
    }

    // Separate path from query/hash so the language prefix is matched against the path only.
    // Otherwise a query string stays glued to the first segment (e.g. "de?x" !== "de"),
    // the locale prefix is not stripped, and localized landing pages fail to resolve.
    const relativeUrl = withRelativePath(url);
    const suffixIndex = relativeUrl.search(/[?#]/);
    const pathPart = suffixIndex >= 0 ? relativeUrl.slice(0, suffixIndex) : relativeUrl;
    const suffix = suffixIndex >= 0 ? relativeUrl.slice(suffixIndex) : '';

    const parts = replaceLangPrefix(pathPart.split(PATH_SEPARATOR).filter(e => e));
    if (parts === null) {
        // Join with a single separator even if the base path has no trailing slash (/root -> /root/catalog).
        const base = getBasePath();
        return (endsWith(base, PATH_SEPARATOR) ? base : base + PATH_SEPARATOR) + ViewName.StoreHub;
    }
    return getBasePath() + parts.join(PATH_SEPARATOR) + suffix;
}

export function updatePathPartsByLangCode(path: string[]) {
    const multiLanguageEnabled = getConfigValue<boolean>(FEATURE_FLAG_MULTILANGUAGE_ENABLED, false);
    if (!multiLanguageEnabled) {
        return path;
    }

    const viewsWithLocaleInPath = getConfigValue(FEATURE_FLAG_MULTILANGUAGE_PAGES, '').split(',');
    const lang = getConfigValue(CONFIG_INTERFACE_CURRENT_LANGUAGE, '');
    const languages: { id: string; slug: string | null }[] = getConfigValue(CONFIG_INTERFACE_LANGUAGES, []);
    const interfaceLang = languages.find(l => l.id === lang);

    const pathPrefix = interfaceLang?.slug;

    const nextPage = path.length > 0 ? path[0].split('?')[0] : '/';
    if (viewsWithLocaleInPath.indexOf(nextPage) >= 0 && pathPrefix) {
        path.unshift(pathPrefix);
    }
    return path;
}

export function updatePathByLangCode(path?: string) {
    const newPath = updatePathPartsByLangCode((path ?? '').split('/'));
    return newPath.join('/');
}

export function getRelevantBasePath(includeLangPart = true): string {
    if (!includeLangPart) {
        return `${window.location.origin}${getBasePath()}`;
    }
    const lang = getConfigValue(CONFIG_INTERFACE_CURRENT_LANGUAGE, '');
    return `${window.location.origin}${updateUlrWithLangCode('', lang)}`;
}

export function normalizeSlashes(path: string): string {
    if (!path) return ''; // Return an empty string if the input is null, undefined, or empty

    return path
        .replace(/\\/g, '/') // Normalize Windows-style backslashes '\' to forward slashes '/'
        .replace(/^\/+/, '') // Remove leading slashes (fixed potential ReDoS issue)
        .replace(/\/+$/, '') // Remove trailing slashes (fixed potential ReDoS issue)
        .replace(/([^:])\/+/g, '$1/'); // Replace multiple consecutive slashes with a single '/', except after 'http(s)://' or 'ftp://'
}

export function joinPaths(...segments: string[]): string {
    let result = '';
    for (const segment of segments) {
        if (!segment) continue;

        const trimmed = normalizeSlashes(segment);

        // Query params or hash? Append without slash
        if (trimmed.startsWith('?') || trimmed.startsWith('#')) {
            result = `${result}${trimmed}`;
            continue;
        }

        // Regular path segment - append with slash separator
        result = result ? `${result}/${trimmed}` : trimmed;
    }
    return result;
}

export function isAbsoluteUrl(url: string): boolean {
    if (!url) return false;

    // Allow only well-known URL schemes
    const allowedProtocols = ['http://', 'https://', 'ftp://', 'file://', 'data:', 'blob:', 'mailto:'];

    return allowedProtocols.some(protocol => url.startsWith(protocol));
}

export function buildAbsoluteUrl(path: string) {
    if (isAbsoluteUrl(path)) {
        return path;
    }
    const fullPath = withAbsolutePath(path);
    return `${window.location.origin}${fullPath}`;
}

/**
 * Build an href for an embeddable link from an already-localized path. In hash
 * mode the path goes into the fragment (`#/...`) so the link works under a host
 * page's URL; otherwise it is a normal path. `absolute` fully-qualifies it with
 * origin (plus basePath in browser mode) for links embedded in HTML strings (e.g.
 * SEO descriptions). Centralizes the href shaping the issuedetails views used to
 * open-code via isHashRouting(), and lets that flag stay private to the model.
 */
export function buildEmbeddableHref(localizedPath: string, { absolute = false }: { absolute?: boolean } = {}): string {
    if (getRoutingMode() === 'hash') {
        const fragment = `#/${localizedPath.replace(/^\/+/, '')}`;
        return absolute ? `${window.location.origin}${window.location.pathname}${fragment}` : fragment;
    }
    return absolute ? `${window.location.origin}${getBasePath()}${localizedPath}` : localizedPath;
}
